import React, { useMemo } from "react";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import CustomButton from "components/common/Button/CustomButton";
import InputField from "components/common/Input/Input";
import SelectOption from "components/common/Select/Select";
import { capitalizeWords, SERVICE_CATEGORY_NAME_MAX_LENGTH } from "Utils/utils";

const toDisplayLabel = (value) => {
  const label = String(value || "").trim();
  if (!label) return "";
  if (label !== label.toLowerCase()) return label;
  return capitalizeWords(label);
};

const ServiceTypeModal = ({
  isOpen,
  onClose,
  editingCategory,
  categoryName,
  setCategoryName,
  categoryFundingType,
  setCategoryFundingType,
  categoryBudgetTypeId,
  setCategoryBudgetTypeId,
  categoryFieldErrors,
  categorySubmitError,
  setCategoryFieldErrors,
  categorySubmitting,
  onSubmit,
  fundingTypes,
  ndisBudgetTypes,
  fundingTypeLabel,
}) => {
  const clearFieldError = (field) => {
    setCategoryFieldErrors((prev) => {
      if (!prev?.[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const fundingTypeSelectedOption = useMemo(
    () => fundingTypes.find((o) => o.value === categoryFundingType) || fundingTypes[0],
    [fundingTypes, categoryFundingType]
  );

  const showBudgetType = editingCategory
    ? editingCategory.funding_type === "NDIS"
    : categoryFundingType === "NDIS";

  const budgetRequired = !editingCategory;

  const budgetTypeDropdownData = useMemo(() => {
    const placeholder = budgetRequired
      ? "Select budget type (required)"
      : "Select budget type (optional)";
    return [
      { value: "", label: placeholder },
      ...ndisBudgetTypes.map((bt) => ({ value: bt.id, label: toDisplayLabel(bt.name) })),
    ];
  }, [ndisBudgetTypes, budgetRequired]);

  const budgetTypeSelectedOption = useMemo(() => {
    if (categoryBudgetTypeId == null || categoryBudgetTypeId === "") {
      return budgetTypeDropdownData[0];
    }
    return (
      budgetTypeDropdownData.find((o) => String(o.value) === String(categoryBudgetTypeId)) ||
      budgetTypeDropdownData[0]
    );
  }, [categoryBudgetTypeId, budgetTypeDropdownData]);

  const modalFooter = () => (
    <div className='flex space-x-2'>
      <CustomButton
        type='button'
        label='Cancel'
        variant='secondary'
        clickHandler={onClose}
        isDisabled={categorySubmitting}
      />
      <CustomButton
        type='button'
        label={editingCategory ? "Update" : "Create"}
        clickHandler={onSubmit}
        showLoading={categorySubmitting}
        isDisabled={categorySubmitting}
      />
    </div>
  );

  if (!isOpen) return null;

  return (
    <ModalSkeleton
      isOpen={isOpen}
      title={editingCategory ? "Edit service type" : "Add service type"}
      closeModal={categorySubmitting ? () => {} : onClose}
      cssClass='w-[25%]'
      modalFooter={modalFooter()}
    >
      <p className='text-sm text-gray-500 mb-4'>
        {editingCategory
          ? "Update the service type name and budget grouping."
          : "Create a service type to group your custom line items."}
      </p>
      {categorySubmitError && (
        <div className='mb-3 p-2 rounded bg-red-50 text-red-700 text-sm'>{categorySubmitError}</div>
      )}
      {editingCategory ? (
        <div className='mb-3'>
          <label className='block text-sm font-medium text-gray-700 mb-1'>Funding type</label>
          <div className='py-1.5 text-sm text-gray-600'>{fundingTypeLabel(editingCategory.funding_type)}</div>
          <p className='text-xs text-gray-500'>Funding type cannot be changed after creation.</p>
        </div>
      ) : (
        <div className='mb-3'>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Funding type
          </label>
          <SelectOption
            data={fundingTypes}
            selectedOption={fundingTypeSelectedOption}
            handleChange={(opt) => {
              setCategoryFundingType(opt?.value ?? "NDIS");
              clearFieldError("funding_type");
            }}
            useMenuPortal
            cssClass='z-10'
          />
          <p className='text-xs text-gray-500 mt-0.5'>
            Service types are grouped by funding type (NDIS, Aged Care, Private, Other).
          </p>
          {categoryFieldErrors.funding_type && (
            <p className='text-red-600 text-xs mt-1'>{categoryFieldErrors.funding_type}</p>
          )}
        </div>
      )}
      <div className='mb-4'>
        <InputField
          name='service_type_name'
          type='text'
          label='Name'
          redstar
          placeHolder='e.g. Aged Care, In Home'
          value={categoryName}
          maxLength={SERVICE_CATEGORY_NAME_MAX_LENGTH}
          handleChange={(val) => {
            setCategoryName(val);
            clearFieldError("name");
          }}
          ErrorMessage={categoryFieldErrors.name}
        />
        <p className='text-xs text-gray-500 mt-0.5'>
          Max {SERVICE_CATEGORY_NAME_MAX_LENGTH} characters. Letters, numbers, hyphens, underscores, and spaces only.
        </p>
      </div>
      {showBudgetType && (
        <div className='mb-4'>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Budget type
            {budgetRequired && <span className='text-red-500 ml-0.5'>*</span>}
          </label>
          <SelectOption
            data={budgetTypeDropdownData}
            selectedOption={budgetTypeSelectedOption}
            handleChange={(opt) => {
              setCategoryBudgetTypeId(
                opt?.value !== undefined && opt?.value !== "" ? parseInt(opt.value, 10) : null
              );
              clearFieldError("ndis_budget_type_id");
            }}
            useMenuPortal
            cssClass='z-10'
          />
          <p className='text-xs text-gray-500 mt-0.5'>
            {budgetRequired
              ? "Required – assigns the category under Core, Capacity Building, or Capital."
              : "Assign to Core, Capacity Building, or Capital to group correctly."}
          </p>
          {categoryFieldErrors.ndis_budget_type_id && (
            <p className='text-red-600 text-xs mt-1'>{categoryFieldErrors.ndis_budget_type_id}</p>
          )}
        </div>
      )}
    </ModalSkeleton>
  );
};

export default ServiceTypeModal;
